import { toaster } from "baseui/toast";
import { debounce, isFunction } from "lodash";
import type { IPromiseBasedObservable } from "mobx-utils";
import type { RefObject } from "react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { FlashMessage } from "state-trees/src/utils";
import type { StyleObject } from "styletron-react";
import type { CombinedError } from "urql";

export interface HubspotWidget {
  status: () => { loaded: boolean };
  refresh: (opts?: { openToNewThread?: boolean }) => void;
  open: () => void;
  load: (options?: { widgetOpen: boolean }) => void;
}

declare global {
  interface Window {
    GadgetMode: "Edit" | "Render" | "Server";
    HubSpotConversations?: {
      widget: HubspotWidget;
    };
    hsConversationsSettings: {
      loadImmediately: boolean;
      identificationEmail?: string;
      identificationToken?: string;
    };
  }
}

export function assert<T>(value: T | undefined | null, message?: string): T {
  if (!value) {
    throw new Error("assertion error" + (message ? `: ${message}` : ""));
  }
  return value;
}

export const timeout = <T,>(timeoutMs: number, promise: Promise<T> | IPromiseBasedObservable<T>, failureMessage?: string) => {
  let timeoutHandle: any = null;
  const timeoutPromise = new Promise<never>((resolve, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(failureMessage)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).then((result) => {
    clearTimeout(timeoutHandle);
    return result;
  });
};

/** Allows the use of multiple refs with one component */
export const multiref = <T,>(...refs: React.Ref<T>[]) => {
  return (value: T) => {
    for (const ref of refs) {
      if (ref) {
        if (isFunction(ref)) {
          ref(value);
        } else {
          (ref as any).current = value;
        }
      }
    }
  };
};

export const getChildrenText = (children: React.ReactNode | React.ReactNode[]) => {
  let label = "";
  React.Children.forEach(children, (child) => {
    if (!child) return;
    if (typeof child === "string") {
      label += child;
    }
    if (typeof child === "object" && "props" in child) {
      if ((typeof child.type === "function" || typeof child.type === "object") && "textContent" in child.type) {
        label += (child.type as any).textContent;
      }

      if (child.props.children) {
        label += getChildrenText(child.props.children);
      }
    }
  });
  return label;
};

type Node<T extends HTMLElement = HTMLElement> = RefObject<T> | null;
interface RefHandlerOptions<T extends HTMLElement = HTMLElement> {
  autoRef?: boolean;
  delay?: number;
  targetRef?: Node<T>;
}
/** A debounced (element) hover state tracker, by ref */
export const useDebouncedHoverWithRef = getDebouncedRefHandlerHook("mouseenter", "mouseleave");
/** A debounced (element) focus state tracker, by ref */
export const useDebouncedFocusWithRef = getDebouncedRefHandlerHook("focus", "blur", true);

function getDebouncedRefHandlerHook(
  activationEvent: keyof HTMLElementEventMap,
  deactivationEvent: keyof HTMLElementEventMap,
  focusable = false
) {
  return <T extends HTMLElement>(options: RefHandlerOptions<T> = {}): [boolean, Node<T>] => {
    const { targetRef, autoRef = false, delay = 300 } = options;
    const [value, setValue] = useState<boolean>(false);
    const ref = targetRef || useRef<T>(null);

    useEffect(() => {
      const handleActivate = debounce(() => setValue(true), delay);
      const handleDeactivate = () => {
        setValue(false);
        handleActivate.cancel();
      };

      const node: T | null = autoRef && focusable ? getFocusableElement<T>(ref?.current) : ref?.current;

      if (node) {
        node.addEventListener(activationEvent, handleActivate);
        node.addEventListener(deactivationEvent, handleDeactivate);
        if (autoRef) {
          node.addEventListener("click", handleDeactivate);
        }
      }

      return () => {
        if (node) {
          node.removeEventListener(activationEvent, handleActivate);
          node.removeEventListener(deactivationEvent, handleDeactivate);
          if (autoRef) {
            node.removeEventListener("click", handleDeactivate);
          }
        }
      };
    }, [ref, autoRef, delay]);

    return [value, ref];
  };
}

function getFocusableElement<T extends HTMLElement>(ref: T | null): T | null {
  if (ref) {
    const focusEl = ref.querySelector("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])");
    if (focusEl) {
      return focusEl as T;
    }
  }
  return ref;
}
export const genericNegativeToaster = (message = "Something went wrong. Please try again in a few seconds.") => {
  toaster.negative(message, { autoHideDuration: 3000 });
};

/**
 * If you have an event handler that needs to run async business logic, you should wrap it in this to make it a synchronous handler that still runs and catches async errors.
 * Runs the `handler` function right away, expecting it to return a promise, and logs any promise rejections.
 * This is used for the same thing as (and serves serves the same purpose as) `safelyRunAsyncOutOfBand` in the Gadget server side code
 **/
export const safelyRunAsyncEventHandler = (handler: () => Promise<void>) => {
  handler().catch((error) => {
    console.error("error encountered running async event handler", error);
    throw error;
  });
};

/**
 * Function wrapper for building a sync event handler out of an async event handler using `safelyRunAsyncEventHandler`
 * Returns a new handler function.
 **/
export const asyncSafeEventHandler = <T extends any[]>(handler: (...args: T) => Promise<void>): ((...args: T) => void) => {
  return (...args) =>
    safelyRunAsyncEventHandler(async () => {
      return await handler(...args);
    });
};

/** Spreadable styles to delay hiding an element—e.g., to make it NOT focusable, AFTER a transition/animation */
export const delayedHide = (isHidden: boolean, delay: number | string = "0.5s"): StyleObject => ({
  animationName: isHidden ? "delayedHide" : "none",
  animationDuration: typeof delay === "number" ? `${delay}ms` : delay,
  animationTimingFunction: "linear",
  animationFillMode: "both",
});

export const flat = (object: Record<string, any>) => {
  const result: Record<string, any> = {};
  function step(object: Record<string, any>, previous?: string) {
    Object.keys(object).forEach(function (key) {
      const value = object[key];
      const type = Object.prototype.toString.call(value);
      const isobject = type === "[object Object]" || type === "[object Array]";

      const newKey = previous ? previous + "." + key : key;

      if (isobject && Object.keys(value).length) {
        return step(value, newKey);
      }

      result[newKey] = value;
    });
  }
  step(object);
  return result;
};

export const isPaymentRequiredError = (error: Error | CombinedError) => {
  return "graphQLErrors" in error && error.graphQLErrors.some((error) => error.message.includes("GGT_PAYMENT_REQUIRED"));
};

export const useDisplayFlashMessage = (flashMessage?: FlashMessage | null) => {
  useEffect(() => {
    let flashKey: React.Key;

    const toastOptions = { autoHideDuration: 4000 };
    if (!flashMessage) return;

    const { type, message } = flashMessage;

    switch (type) {
      case "error":
        flashKey = toaster.negative(message, toastOptions);
        break;
      case "warning":
        flashKey = toaster.warning(message, toastOptions);
        break;
      case "info":
        flashKey = toaster.info(message, toastOptions);
        break;
      case "success":
        flashKey = toaster.positive(message, toastOptions);
        break;
    }

    return () => toaster.update(flashKey, { autoHideDuration: 1 });
  }, [flashMessage]);
};

export const useIdentifiedSupportConversation = (
  loadUserIdentification: () => Promise<{ email?: string; token?: string } | undefined>,
  opts: { loadImmediately?: boolean; currentUser?: { createdDate: string } } = {}
) => {
  const [loaded, setLoaded] = useState(false);
  const identifyPromise = useRef<Promise<void>>(Promise.resolve());
  const mounted = useRef(true);

  const loadWidget = useCallback(async () => {
    await identifyPromise.current;
    window.HubSpotConversations?.widget.load();
    if (mounted.current) {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (opts.loadImmediately && !loaded) {
      safelyRunAsyncEventHandler(loadWidget);
    }
  }, [loadWidget, loaded, opts.loadImmediately]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    identifyPromise.current = loadUserIdentification()
      .then((result) => {
        if (result && result.email) {
          window.hsConversationsSettings.identificationEmail = result.email;
        }

        if (result && result.token) {
          window.hsConversationsSettings.identificationToken = result.token;
        }
      })
      .catch((err) => {
        console.log(err);
      });

    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [loaded, loadWidget] as const;
};
